import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Category, CategoryInput,
  listCategories, createCategory, updateCategory, deleteCategory,
} from '../services/categoriesApi';

interface CategoriesState {
  items: Category[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchList: () => Promise<void>;
  create: (input: CategoryInput) => Promise<Category>;
  update: (id: number, input: Partial<CategoryInput>) => Promise<Category>;
  remove: (id: number) => Promise<void>;
}

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useCategoriesStore = create<CategoriesState>()(
  devtools(
    (set, get) => ({
      items: [],
      loading: false,
      saving: false,
      error: null,
      fetchList: async () => {
        set({ loading: true, error: null });
        try {
          const data = await listCategories();
          set({ items: data, loading: false });
        } catch (err) {
          set({ loading: false, error: toErr(err) });
        }
      },
      create: async (input) => {
        set({ saving: true, error: null });
        try {
          const created = await createCategory(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      update: async (id, input) => {
        set({ saving: true, error: null });
        try {
          const updated = await updateCategory(id, input);
          set((s) => ({
            items: s.items.map((it) => (it.id === id ? updated : it)),
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      remove: async (id) => {
        set({ saving: true, error: null });
        try {
          await deleteCategory(id);
          set((s) => ({ items: s.items.filter((it) => it.id !== id), saving: false }));
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
    }),
    { name: 'categories-store' }
  )
);
